import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CurrencyDollarIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ApiCustom from "../../utils/ApiCustom";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import CustomTableComponent from "../../components/CustomTableComponent";

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

const formatMoney = (n) =>
  n ? Number(n).toLocaleString("vi-VN") + " ₫" : "0 ₫";

// --- Modal chi tiết thống kê giao dịch ---
const TransactionStatisticsModal = ({
  isOpen,
  onClose,
  filterStart,
  filterEnd,
  setFilterStart,
  setFilterEnd,
  loading,
  chartData,
  totalValue,
  companyList,
  tableLoading,
}) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        {...animation}
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
      >
        <motion.div
          {...animation}
          className="relative bg-white w-full max-w-5xl mx-4 rounded-2xl p-8 shadow-xl border border-emerald-100"
          style={{ maxHeight: "92vh", overflow: "auto" }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div>
              <h2 className="text-lg font-semibold mb-1">
                Thống kê giao dịch theo hội viên
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-sm text-gray-500">Từ</span>
                <input
                  type="date"
                  value={filterStart}
                  max={filterEnd}
                  onChange={(e) => setFilterStart(e.target.value)}
                  className="border border-gray-200 rounded px-2 py-1 text-sm bg-gray-50 text-gray-700"
                />
                <span className="text-sm text-gray-500">đến</span>
                <input
                  type="date"
                  value={filterEnd}
                  min={filterStart}
                  max={dayjs().format("YYYY-MM-DD")}
                  onChange={(e) => setFilterEnd(e.target.value)}
                  className="border border-gray-200 rounded px-2 py-1 text-sm bg-gray-50 text-gray-700"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2 md:mt-0">
              <span className="text-sm text-gray-500">Tổng giá trị:</span>
              <span className="font-bold text-emerald-700 text-xl">
                {loading
                  ? "..."
                  : Number(totalValue) === 0
                    ? "Đang cập nhật"
                    : formatMoney(totalValue)}
              </span>
            </div>
          </div>

          <div className="mb-8">
            <div className="font-semibold text-emerald-600 mb-2 flex items-center gap-2">
              <CurrencyDollarIcon className="h-6 w-6" />
              Giá trị giao dịch theo thời gian
            </div>
            <div className="bg-gray-50 rounded-xl border px-2 py-3">
              {loading ? (
                <div className="text-gray-400 text-center py-8">
                  Đang tải dữ liệu...
                </div>
              ) : chartData.length === 0 ? (
                <div className="text-gray-400 text-center py-8">
                  Không có dữ liệu
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData}>
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={formatMoney} width={90} />
                    <Tooltip formatter={formatMoney} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name="Tổng giá trị"
                      stroke="#10b981"
                      fill="#d1fae5"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div>
            <div className="font-semibold mb-3 text-blue-700 flex items-center gap-2">
              <UsersIcon className="h-5 w-5" /> Bảng tổng hợp giao dịch hội viên
            </div>

            <div style={{ maxHeight: 340, overflowY: "auto" }}>
              {tableLoading ? (
                <div className="text-center py-6 text-gray-400">
                  Đang tải...
                </div>
              ) : (
                <CustomTableComponent
                  headers={[
                    { key: "companyName", label: "Tên hội viên" },
                    {
                      key: "totalValue",
                      label: "Tổng giá trị",
                      render: (item) => (
                        <span className="font-semibold text-emerald-700">
                          {Number(item.totalValue) === 0
                            ? "Giá liên hệ"
                            : formatMoney(item.totalValue)}
                        </span>
                      ),
                    },
                  ]}
                  data={companyList}
                />
              )}

              {companyList.length === 0 && !tableLoading && (
                <div className="text-center text-sm text-gray-400 my-4">
                  Không có giao dịch trong khoảng thời gian này.
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// --- Card tổng giá trị giao dịch ---
const TransactionValueCard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterStart, setFilterStart] = useState(
    dayjs().startOf("year").format("YYYY-MM-DD"),
  );
  const [filterEnd, setFilterEnd] = useState(dayjs().format("YYYY-MM-DD"));

  const [loading, setLoading] = useState(true);
  const [totalValue, setTotalValue] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [cardLoading, setCardLoading] = useState(true);
  const [cardTotal, setCardTotal] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    let ignore = false;

    async function fetchTransactionStats() {
      setLoading(true);
      setTableLoading(true);
      setTotalValue(0);
      setChartData([]);
      setCompanyList([]);

      try {
        const res = await ApiCustom.get("/order-show");
        let orders = res?.data?.data?.order || [];

        orders = orders.filter((o) => {
          const created = dayjs(o.createdAt).format("YYYY-MM-DD");
          return (
            created >= filterStart &&
            created <= filterEnd &&
            ["success", "approved", "Đã duyệt"].includes(o.status)
          );
        });

        const ordersWithDetails = await Promise.all(
          orders.map((order) =>
            ApiCustom.get(`/order-show-id/${order.id}`)
              .then((detailRes) => ({
                ...order,
                details: detailRes?.data?.data?.details || [],
              }))
              .catch(() => ({
                ...order,
                details: [],
              })),
          ),
        );

        const companyTotals = {};
        const companyHasContactPrice = {};
        const chartObj = {};

        for (const order of ordersWithDetails) {
          const details = order.details || [];
          const detail = details[0];
          if (!detail || !detail.product?.companyId) continue;

          const companyId = detail.product.companyId;
          const hasContactPriceInOrder = details.some(
            (d) => d?.product?.isContactPrice === true,
          );

          companyTotals[companyId] =
            (companyTotals[companyId] || 0) + Number(order.totalPrice || 0);

          if (hasContactPriceInOrder) {
            companyHasContactPrice[companyId] = true;
          }

          const d = dayjs(order.createdAt).format("YYYY-MM-DD");
          chartObj[d] = (chartObj[d] || 0) + Number(order.totalPrice || 0);
        }

        const companyIds = [
          ...new Set([
            ...Object.keys(companyTotals),
            ...Object.keys(companyHasContactPrice),
          ]),
        ];

        const companyInfoMap = {};
        await Promise.all(
          companyIds.map((id) =>
            ApiCustom.get(`/company-show-by-id/${id}`)
              .then((res) => {
                const name = res?.data?.data?.companyName || `Hội viên #${id}`;
                companyInfoMap[id] = name;
              })
              .catch(() => {
                companyInfoMap[id] = `Hội viên #${id}`;
              }),
          ),
        );

        const companyListData = companyIds
          .map((id) => ({
            companyName: companyInfoMap[id],
            totalValue: Number(companyTotals[id] || 0),
          }))
          .sort((a, b) => b.totalValue - a.totalValue);

        const chartArr = Object.entries(chartObj)
          .map(([date, value]) => ({ date, value }))
          .sort((a, b) => a.date.localeCompare(b.date));

        const total = companyListData.reduce(
          (sum, row) => sum + Number(row.totalValue || 0),
          0,
        );

        if (!ignore) {
          setChartData(chartArr);
          setTotalValue(total);
          setCompanyList(companyListData);
        }
      } catch {
        if (!ignore) {
          setChartData([]);
          setTotalValue(0);
          setCompanyList([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
          setTableLoading(false);
        }
      }
    }

    fetchTransactionStats();
    return () => {
      ignore = true;
    };
  }, [isOpen, filterStart, filterEnd]);

  useEffect(() => {
    let ignore = false;

    async function fetchCardTotal() {
      setCardLoading(true);
      try {
        const res = await ApiCustom.get("/order-show");
        let orders = res?.data?.data?.order || [];

        orders = orders.filter((o) => {
          const created = dayjs(o.createdAt).format("YYYY-MM-DD");
          return (
            created >= dayjs().startOf("year").format("YYYY-MM-DD") &&
            created <= dayjs().format("YYYY-MM-DD") &&
            ["success", "approved", "Đã duyệt"].includes(o.status)
          );
        });

        const ordersWithDetails = await Promise.all(
          orders.map((order) =>
            ApiCustom.get(`/order-show-id/${order.id}`)
              .then((detailRes) => ({
                ...order,
                details: detailRes?.data?.data?.details || [],
              }))
              .catch(() => ({
                ...order,
                details: [],
              })),
          ),
        );

        let total = 0;
        for (const order of ordersWithDetails) {
          const detail = order.details?.[0];
          if (!detail || !detail.product?.companyId) continue;
          total += Number(order.totalPrice || 0);
        }

        if (!ignore) setCardTotal(total);
      } catch {
        if (!ignore) setCardTotal(0);
      } finally {
        if (!ignore) setCardLoading(false);
      }
    }

    fetchCardTotal();
    return () => {
      ignore = true;
    };
  }, []);

  return (
    <div className="w-full">
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full h-full shadow-md sm:w-auto flex flex-col items-center text-center p-5 rounded-2xl border border-emerald-100 bg-white transition-all cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <div className="p-3 bg-emerald-50 rounded-full mb-3">
          <CurrencyDollarIcon className="h-6 w-6 text-emerald-600" />
        </div>
        <p className="text-lg text-black mb-1">Tổng giá trị giao dịch</p>
        <p className="text-3xl font-bold">
          {cardLoading ? "..." : formatMoney(cardTotal)}
        </p>
      </motion.div>

      <TransactionStatisticsModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        filterStart={filterStart}
        filterEnd={filterEnd}
        setFilterStart={setFilterStart}
        setFilterEnd={setFilterEnd}
        loading={loading}
        chartData={chartData}
        totalValue={totalValue}
        companyList={companyList}
        tableLoading={tableLoading}
      />
    </div>
  );
};

export default TransactionValueCard;
